"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, TriangleAlert, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function WarrantySupport() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="warranty-technical-support" data-nav="light" className="relative w-full bg-background py-16 md:py-24 border-t border-border text-foreground">
      <div className="mx-auto max-w-7xl px-5 sm:px-10 lg:px-16">
        {/* Header Block */}
        <div className="mb-12 border-b border-border pb-8">
          <div className="max-w-4xl">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight uppercase leading-tight">
              Check the Warranty for the Exact Mechanical Bull
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Warranty terms can vary by machine, manufacturer, condition and seller. Therefore, buyers should review the warranty information that actually applies to the specific product rather than assuming every mechanical bull receives the same coverage.
            </p>
          </div>
        </div>

        {/* Two-Column Comparison Structure */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          {/* Column A: New Mechanical Bulls */}
          <motion.div variants={itemVariants} className="h-full">
            <Card className="h-full border border-border bg-card flex flex-col justify-between">
              <CardHeader className="border-b border-border pb-5 pt-6">
                <div className="flex items-center justify-between gap-4 mb-2">
                  <span className="font-mono text-xs font-semibold uppercase bg-foreground text-background px-2.5 py-1">
                    Equipment Category
                  </span>
                </div>
                <CardTitle className="font-serif text-2xl sm:text-3xl uppercase tracking-wide">
                  New Mechanical Bulls
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6 text-sm flex-1">
                <div className="flex items-start gap-3">
                  <Info className="size-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-muted-foreground text-base leading-relaxed">
                    For new equipment, check the individual product information or order documentation for any manufacturer or seller warranty that genuinely applies.
                  </p>
                </div>
              </CardContent>
              <div className="p-6 pt-0 mt-auto border-t border-border">
                <Button asChild variant="outline" className="w-full uppercase font-serif tracking-wider text-base py-5 border-border hover:border-foreground">
                  <Link to="/NewMechanicalBullsPage">
                    View New Bulls
                    <ArrowUpRight className="size-4 ml-1 text-muted-foreground" />
                  </Link>
                </Button>
              </div>
            </Card>
          </motion.div>

          {/* Column B: Used Mechanical Bulls */}
          <motion.div variants={itemVariants} className="h-full">
            <Card className="h-full border border-border bg-card flex flex-col justify-between">
              <CardHeader className="border-b border-border pb-5 pt-6">
                <div className="flex items-center justify-between gap-4 mb-2">
                  <span className="font-mono text-xs font-semibold uppercase bg-muted border border-border text-foreground px-2.5 py-1">
                    Equipment Category
                  </span>
                </div>
                <CardTitle className="font-serif text-2xl sm:text-3xl uppercase tracking-wide">
                  Used Mechanical Bulls
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6 text-sm flex-1">
                <div className="flex items-start gap-3">
                  <Info className="size-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-muted-foreground text-base leading-relaxed">
                    Do not assume a used bull carries the same warranty as a new machine. Review the individual listing and order information to understand what, if any, coverage applies to that exact unit.
                  </p>
                </div>
              </CardContent>
              <div className="p-6 pt-0 mt-auto border-t border-border">
                <Button asChild variant="outline" className="w-full uppercase font-serif tracking-wider text-base py-5 border-border hover:border-foreground">
                  <Link to="/UsedMechanicalBullsPage">
                    View Used Bulls
                    <ArrowUpRight className="size-4 ml-1 text-muted-foreground" />
                  </Link>
                </Button>
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Closing Advisory Notice */}
        <div className="border border-border bg-muted/40 p-6 sm:p-7 flex items-start gap-4">
          <TriangleAlert className="size-5 text-primary shrink-0 mt-0.5" />
          <p className="text-sm sm:text-base text-foreground font-medium leading-relaxed">
            If warranty information is not stated, ask before purchasing rather than assuming coverage exists.
          </p>
        </div>
      </div>
    </section>;
}